import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationUserInvitationCacheKey,
    getOrganizationUserInvitationsCacheKey,
    longTermCacheStaleTime,
    onUpdateOrganizationUserInvitations,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationUserInvitations', {
    invitationsLoadFailure: 'Failed to load invitation',
    createInvitationSuccess: 'Invitation created',
    createInvitationFailure: 'Failed to create invitation',
    deleteInvitationSuccess: 'Invitation deleted',
    deleteInvitationFailure: 'Failed to delete invitation',
    deleteInvitationsSuccess: 'Invitations deleted',
    deleteInvitationsFailure: 'Failed to delete invitations',
    invitationLoadFailure: 'Failed to load invitation',
    resendInvitationEmailSuccess: 'Invitation email resent successfully',
    resendInvitationEmailFailure: 'Failed to resend invitation email',
});

export type UseOrganizationInvitationsArgs = {
    onCreateInvitation?: () => void;
    onDeleteInvitationSuccess?: () => void;
    onDeleteInvitationsSuccess?: () => void;
    invitationId?: string;
};

const emptyArray: any[] = [];

export type UseOrganizationInvitationsReturnValue = {
    invitations: APITypes.organizationService.OrganizationUserInvitationDetails[];
    isLoadingInvitations: boolean;
    isFetchingInvitations: boolean;
    createInvitation: (invitation: APITypes.organizationService.CreateOrganizationUserInvitationRequest) => void;
    isCreatingInvitation: boolean;
    deleteInvitation: (invitationId: string) => void;
    isDeletingInvitation: boolean;
    deleteInvitations: (invitationIds: string[]) => void;
    isDeletingInvitations: boolean;
    invitation: APITypes.organizationService.GetOrganizationUserInvitationResponse | null;
    isLoadingInvitation: boolean;
    resendInvitationEmail: (invitationId: string) => void;
    isResendingInvitationEmail: boolean;
};

export default function useOrganizationInvitations({
    onCreateInvitation,
    onDeleteInvitationSuccess,
    onDeleteInvitationsSuccess,
    invitationId,
}: UseOrganizationInvitationsArgs): UseOrganizationInvitationsReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const invitationsQuery = useQuery({
        queryKey: getOrganizationUserInvitationsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationUserInvitationsResponse>(
                urls.api.organizations.userInvitations.userInvitations(organizationId),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(invitationsQuery, intl('invitationsLoadFailure'));

    const invitationQuery = useQuery({
        queryKey: invitationId ? getOrganizationUserInvitationCacheKey(invitationId) : [],
        queryFn: async () => {
            const value = await appAPI.get<APITypes.organizationService.GetOrganizationUserInvitationResponse>(
                urls.api.organizations.userInvitations.userInvitation(organizationId, invitationId!),
            );

            return value;
        },
        enabled: !!invitationId,
    });
    useQueryErrorNotify(invitationQuery, intl('invitationLoadFailure'));

    const createOrganizationInvitationMutation = useMutation({
        mutationFn: async (invitation: APITypes.organizationService.CreateOrganizationUserInvitationRequest) => {
            return await appAPI.post(urls.api.organizations.userInvitations.userInvitations(organizationId), {
                body: invitation,
            });
        },
        onSuccess: () => {
            onUpdateOrganizationUserInvitations.resetCache({})();
            notify('success', intl('createInvitationSuccess'));
            onCreateInvitation?.();
        },
        onError: () => {
            notify('error', intl('createInvitationFailure'));
        },
    });

    const deleteInvitationMutation = useMutation({
        mutationFn: async (invitationId: string) => {
            return await appAPI.del(
                urls.api.organizations.userInvitations.userInvitation(organizationId, invitationId),
            );
        },
        onSuccess: () => {
            onUpdateOrganizationUserInvitations.resetCache({})();
            notify('success', intl('deleteInvitationSuccess'));
            onDeleteInvitationSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteInvitationFailure'));
        },
    });

    const deleteInvitationsMutation = useMutation({
        mutationFn: async (invitationIds: string[]) => {
            for (const invitationId of invitationIds) {
                await appAPI.del(urls.api.organizations.userInvitations.userInvitation(organizationId, invitationId));
            }
        },
        onSuccess: () => {
            onUpdateOrganizationUserInvitations.resetCache({})();
            notify('success', intl('deleteInvitationSuccess'));
            onDeleteInvitationsSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteInvitationsFailure'));
        },
    });

    const resendInvitationEmailMutation = useMutation({
        mutationFn: async (invitationId: string) => {
            return await appAPI.post(
                urls.api.organizations.userInvitations.resendInvitationEmail(organizationId, invitationId),
            );
        },
        onSuccess: () => {
            notify('success', intl('resendInvitationEmailSuccess'));
        },
        onError: (e) => {
            console.error(e);

            notify('error', intl('resendInvitationEmailFailure'));
        },
    });

    return {
        invitations: invitationsQuery.data?.items || emptyArray,
        isLoadingInvitations: invitationsQuery.isLoading,
        isFetchingInvitations: invitationsQuery.isFetching,
        createInvitation: createOrganizationInvitationMutation.mutate,
        isCreatingInvitation: createOrganizationInvitationMutation.isPending,
        deleteInvitation: deleteInvitationMutation.mutate,
        isDeletingInvitation: deleteInvitationMutation.isPending,
        deleteInvitations: deleteInvitationsMutation.mutate,
        isDeletingInvitations: deleteInvitationsMutation.isPending,
        invitation: invitationQuery.data || null,
        isLoadingInvitation: invitationQuery.isLoading,
        resendInvitationEmail: resendInvitationEmailMutation.mutate,
        isResendingInvitationEmail: resendInvitationEmailMutation.isPending,
    };
}
